import React, { FC, useContext, useEffect } from 'react';

import { Grid, Table } from '@material-ui/core';

import { GlobalContext } from '../../../../../store';
import { ScoreType } from '../../../../../store/admin/eventFormats/store';
import { TIFormatPrefix } from '../../../../../store/admin/roundFormats/store';
import { IMetaResult } from '../../../../../store/admin/roundResults/store';

import { capitalizeFirstLetter } from '../../../../../contrib/utils';
import { sortBy } from '../../../../../contrib/sortBy';
import {
  useAssignResultsDispatch,
  useAssignResultsState
} from '../AssignResults/Context/AssignResults.context';
import { getResultFieldName } from './Utils/getResultFieldName';
import ResultsTableBody from './Components/ResultsTableBody';
import ResultsTableHead from './ResultsTableHead';
import { useRouteInfoState } from '../../../../../components/RouteInfo/Context/RouteInfo.context';

const ResultsTable: FC = () => {
  const { eventId, roundId } = useRouteInfoState();

  const {
    results,
    selectedCombination,
    sortedResults,
    selectedTeamFormat
  } = useAssignResultsState();

  const {
    dispatch,
    actions: { setHorseRaceEliminatedResults, setCTPResults }
  } = useAssignResultsDispatch();

  const {
    actions: {
      // adminEventFormatAwards: { get: getEventFormatAwards },
      adminEventResults: { get: getEventResults },
      // adminRoundFormatAwards: { get: getRoundFormatAwards },
      adminRoundResults: { get: getRoundResults },
      adminHoleResults: { get: getHoleResults },
      adminTeamResults: { get: getTeamResults }
    },
    adminEventFormatAwards,
    // adminEventFormats,
    adminEventResults,
    adminHoleResults,
    adminRoundFormatAwards,
    adminTeamResults,
    adminRoundResults
  } = useContext(GlobalContext);

  useEffect(() => {
    if (selectedCombination) {
      if (roundId) {
        getRoundResults(roundId);
      } else {
        getEventResults(eventId);
      }
      getHoleResults(roundId);
    }
  }, [roundId, eventId, selectedCombination]);

  // Disable awards for now
  // useEffect(() => {
  //   if (roundId && selectedCombination) {
  //     getRoundFormatAwards(roundId, selectedCombination.id)
  //       .then()
  //       .catch();
  //   }
  // }, [roundId, selectedCombination]);

  // useEffect(() => {
  //   if (!roundId && clubId && eventId && adminEventFormats && adminEventFormats[Number(eventId)]) {
  //     asyncForEach(adminEventFormats[Number(eventId)], (eventFormat: IMetaFormat) =>
  //       getEventFormatAwards(clubId, eventId, eventFormat.id)
  //     )
  //       .then()
  //       .catch();
  //   }
  // }, [clubId, eventId, adminEventFormats]);

  useEffect(() => {
    if (selectedCombination && adminRoundResults && roundId && adminRoundResults[Number(roundId)]) {
      dispatch({
        type: 'setResults',
        value: selectedCombination.flight
          ? adminRoundResults[Number(roundId)][selectedCombination.flight.id]
            ? adminRoundResults[Number(roundId)][selectedCombination.flight.id]
            : []
          : Object.values(adminRoundResults[Number(roundId)]).flat()
      });
    } else {
      dispatch({
        type: 'setResults',
        value: []
      });
    }
  }, [adminRoundResults, selectedCombination, roundId]);

  useEffect(() => {
    if (!roundId) {
      if (
        selectedCombination &&
        adminEventResults &&
        eventId &&
        adminEventResults[Number(eventId)]
      ) {
        dispatch({
          type: 'setResults',
          value: selectedCombination.flight
            ? adminEventResults[Number(eventId)][selectedCombination.flight.id]
              ? adminEventResults[Number(eventId)][selectedCombination.flight.id]
              : []
            : Object.values(adminEventResults[Number(eventId)]).flat()
        });
      } else {
        dispatch({
          type: 'setResults',
          value: []
        });
      }
    }
  }, [adminEventResults, selectedCombination, eventId]);

  useEffect(() => {
    if (selectedCombination && adminHoleResults && roundId && adminHoleResults[Number(roundId)]) {
      if (selectedCombination.format.format.prefix === 'skins') {
        const {
          format: {
            considerFlights,
            format: { prefix: selectedFormatPrefix }
          }
        } = selectedCombination;

        const processedPrefix = `${selectedFormatPrefix}${
          considerFlights ? 'Flight' : 'Round'
        }` as TIFormatPrefix;

        const winnerFieldName = getResultFieldName(
          processedPrefix,
          selectedCombination.scoreType,
          'Winner'
        ) as keyof IMetaResult;

        const participatingFieldName = getResultFieldName(
          processedPrefix,
          selectedCombination.scoreType,
          'Participating'
        ) as keyof IMetaResult;

        const holeResults = adminHoleResults[Number(roundId)]
          ? sortBy(
              adminHoleResults[Number(roundId)].filter(
                result =>
                  result[winnerFieldName] &&
                  result[participatingFieldName] &&
                  (selectedCombination.format.considerFlights
                    ? selectedCombination.flight &&
                      result.flightId === selectedCombination.flight.id
                    : true)
              ),
              'asc',
              'hole'
            )
          : [];

        dispatch({
          type: 'setResults',
          value: holeResults
        });
      }
    } else {
      dispatch({
        type: 'setResults',
        value: []
      });
    }
  }, [adminHoleResults, selectedCombination, roundId]);

  useEffect(() => {
    if (
      adminRoundFormatAwards &&
      roundId &&
      adminRoundFormatAwards[Number(roundId)] &&
      selectedCombination &&
      adminRoundFormatAwards[Number(roundId)][selectedCombination.id]
    ) {
      dispatch({
        type: 'setUserAwards',
        value: adminRoundFormatAwards[Number(roundId)][selectedCombination.id]
      });
    }
  }, [adminRoundFormatAwards, selectedCombination, roundId]);

  useEffect(() => {
    if (
      adminEventFormatAwards &&
      eventId &&
      adminEventFormatAwards[Number(eventId)] &&
      selectedCombination &&
      adminEventFormatAwards[Number(eventId)][selectedCombination.id]
    ) {
      dispatch({
        type: 'setUserAwards',
        value: adminEventFormatAwards[Number(eventId)][selectedCombination.id]
      });
    }
  }, [adminEventFormatAwards, selectedCombination, eventId]);

  useEffect(() => {
    dispatch({ type: 'sortResults' });
  }, [results, selectedCombination]);

  useEffect(() => {
    if (!!selectedCombination) {
      setHorseRaceEliminatedResults(selectedCombination.format, selectedCombination.scoreType);
    }
  }, [selectedCombination, sortedResults]);

  useEffect(() => {
    if (
      roundId &&
      selectedCombination &&
      selectedCombination.format.format.prefix === 'teamstroke'
    ) {
      getTeamResults('round', roundId);
    }
  }, [selectedCombination, roundId]);

  useEffect(() => {
    const flightId =
      selectedCombination && selectedCombination.flight ? selectedCombination.flight.id : null;

    if (
      selectedCombination &&
      flightId &&
      roundId &&
      Object.keys(adminTeamResults).length > 0 &&
      adminTeamResults[Number(roundId)] &&
      adminTeamResults[Number(roundId)][flightId]
    ) {
      const { scoreType: selectedScoreType } = selectedCombination;

      const scoreType = capitalizeFirstLetter(ScoreType[selectedScoreType].toLowerCase());
      const teamFormatScoreType = `${selectedTeamFormat}${scoreType}`;
      const positionField = `${teamFormatScoreType}Position`;

      const teamResults = adminTeamResults[Number(roundId)][flightId];

      dispatch({
        type: 'setTeamResults',
        value: sortBy(teamResults, 'asc', positionField)
      });
    }
  }, [adminTeamResults, roundId, selectedCombination]);

  useEffect(() => {
    if (selectedCombination && selectedCombination.format.format.prefix.includes('ctp')) {
      setCTPResults(adminHoleResults, Number(roundId), selectedCombination.format.considerFlights);
    }
  }, [adminHoleResults, selectedCombination]);

  const isGBNorCTP =
    selectedCombination &&
    (selectedCombination.format.format.prefix.includes('gbn') ||
      selectedCombination.format.format.prefix.includes('ctp'));

  return (
    <div className="results-table">
      {selectedCombination ? (
        <>
          <Grid className="grid combination-information" container={true} spacing={8}>
            <Grid className="grid-item" item={true} xs={12}>
              <p className="formatName">{selectedCombination.format.format.name}</p>

              {!isGBNorCTP && (
                <p className="scoreTypeName">{ScoreType[selectedCombination.scoreType]}</p>
              )}
            </Grid>
          </Grid>

          <Table className="results-table-table">
            <colgroup>
              {[0, 1, 2, 3, 4, 6, 7].map(index => (
                <col key={index} />
              ))}
            </colgroup>

            <ResultsTableHead />

            <ResultsTableBody />
          </Table>
        </>
      ) : (
        <p className="noResults">
          Please, select a combination from the list below the filters on the left area
        </p>
      )}
    </div>
  );
};

export default ResultsTable;
